/**
 * 督導例外通道 modal — Flow 4.5 / kiosk-screens E1-supervisor-pin。
 *
 * UI scaffold：後端 D-2 manual_override 端點尚未開放（supervisor-override-
 * metric.service 為觀測，非執行）。本 modal 收原因 + 4 位 PIN，按下確認
 * 後顯示「已記錄」訊息並關閉。日後串接時把 onConfirm 換成真實 API call。
 */

import { useState } from 'react';

import { KioskButton } from './KioskButton';
import { useKiosk } from '../lib/session';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SupervisorPinModal({ open, onClose }: Props): JSX.Element | null {
  const { t } = useKiosk();
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const canSubmit = reason.trim().length >= 5 && /^\d{4}$/.test(pin);

  function reset(): void {
    setReason('');
    setPin('');
    setSubmitted(false);
  }

  function submit(): void {
    if (!canSubmit) return;
    // TODO(D-2): POST /supervisor/override { reason, pin, context }
    setSubmitted(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('supervisor.modalTitle')}
      data-testid="kiosk-supervisor-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          reset();
          onClose();
        }
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          padding: 36,
          maxWidth: 720,
          width: '100%',
          borderTop: '6px solid var(--c-danger)',
        }}
      >
        <h2 className="k-h1" style={{ color: 'var(--c-danger)' }}>
          {t('supervisor.modalTitle')}
        </h2>
        <p className="k-text" style={{ color: 'var(--c-danger)', fontWeight: 600 }}>
          {t('supervisor.warning')}
        </p>

        {submitted ? (
          <>
            <p className="k-text" style={{ marginTop: 16 }}>
              {t('supervisor.stub')}
            </p>
            <div className="k-footbar" style={{ marginTop: 24 }}>
              <KioskButton
                variant="primary"
                block
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                {t('common.confirm')}
              </KioskButton>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginTop: 20 }}>
              <label className="k-text" style={{ fontWeight: 600 }}>
                {t('supervisor.reasonLabel')}
              </label>
              <textarea
                className="k-input"
                rows={2}
                placeholder={t('supervisor.reasonPlaceholder')}
                value={reason}
                style={{ marginTop: 10, resize: 'none' }}
                onChange={(e) => setReason(e.target.value)}
                data-testid="kiosk-supervisor-reason"
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <label className="k-text" style={{ fontWeight: 600 }}>
                {t('supervisor.pinLabel')}
              </label>
              <input
                className="k-input"
                type="password"
                inputMode="numeric"
                maxLength={4}
                pattern="\d{4}"
                placeholder="••••"
                value={pin}
                style={{ marginTop: 10, letterSpacing: 12, fontSize: 'var(--fs-h2)' }}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                data-testid="kiosk-supervisor-pin"
              />
            </div>
            <div className="k-footbar" style={{ marginTop: 24 }}>
              <KioskButton
                variant="secondary"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                {t('common.cancel')}
              </KioskButton>
              <KioskButton
                variant="warning"
                block
                disabled={!canSubmit}
                onClick={submit}
                data-testid="kiosk-supervisor-submit"
              >
                {t('supervisor.submit')}
              </KioskButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
